import { LoadingOutlined } from '@ant-design/icons';
import { Markdown, useChatContext } from '@copilotkit/react-ui';
import React, { useState, useEffect } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import BuglyCopilotProcessIcon from '@/assets/images/copilot/process.svg?react';
import SuccessIcon from './icons/success.react.svg?react';
import styles from './index.module.scss';


const CodeBlock = ({ children, className, ...props }: any) => {
  const code = String(children).replace(/\n$/, '');
  const match = /language-(\w+)/.exec(className || '');

  // 检测是否为内联代码的多种方式
  const isInline = props.inline !== undefined ? props.inline
    : !className // 没有className通常表示内联代码
                   || !code.includes('\n'); // 不包含换行符通常表示内联代码

  if (isInline) {
    return <code style={atomOneDark} className={className}>{children}</code>;
  }

  return (
    <SyntaxHighlighter
      language={match?.[1]}
      style={atomOneDark}
      customStyle={{ margin: 0, borderRadius: '0 0 4px 4px' }}
    >
      {code}
    </SyntaxHighlighter>
  );
};

export const CustomAssistantMessage = (props: any) => {
  const { icons } = useChatContext();
  const {
    message,
    isLoading,
    isGenerating,
    isCurrentMessage,
    markdownTagRenderers,
  } = props;
  const [collapsed, setCollapsed] = useState(false);

  const LoadingIcon = () => <span>{icons.activityIcon}</span>;
  const content = message?.content || '';
  const subComponent = message?.generativeUI?.();

  // 解析 REASONING、PLANNING、FINAL_ANSWER 和 ACTION 标记（使用 /* */ 格式）
  const parseContent = (text: string) => {
    const reasoningRegex = /\/\*REASONING\*\/([\s\S]*?)\/\*REASONING\*\//g;
    const planningRegex = /\/\*PLANNING\*\/([\s\S]*?)\/\*PLANNING\*\//g;
    const actionRegex = /\/\*ACTION\*\/([\s\S]*?)\/\*ACTION\*\//g;

    let processedText = text;
    const reasoningBlocks: Array<{ content: string; isComplete: boolean }> = [];
    const planningBlocks: Array<{ content: string; isComplete: boolean }> = [];
    let hasFinalAnswer = false;

    // 检查是否包含 FINAL_ANSWER 标记
    if (text.includes('/*FINAL_ANSWER*/')) {
      hasFinalAnswer = true;
      // 移除 FINAL_ANSWER 标记，保留其内容
      processedText = processedText.replace(/\/\*FINAL_ANSWER\*\//g, '');
    }

    // 移除 ACTION 标记及其内容
    processedText = processedText.replace(actionRegex, '');

    // 提取 REASONING 内容
    let match;
    while ((match = reasoningRegex.exec(text)) !== null) {
      reasoningBlocks.push({
        content: match[1].trim(),
        isComplete: true, // 已闭合的标记表示完成
      });
      processedText = processedText.replace(match[0], '');
    }

    // 提取 PLANNING 内容
    while ((match = planningRegex.exec(text)) !== null) {
      planningBlocks.push({
        content: match[1].trim(),
        isComplete: true,
      });
      processedText = processedText.replace(match[0], '');
    }

    // 检查是否有未闭合的标记（正在生成中）
    const openReasoningMatch = text.match(/\/\*REASONING\*\/([\s\S]*?)$/);
    if (openReasoningMatch && !text.includes('/*REASONING*/', openReasoningMatch.index! + 13)) {
      reasoningBlocks.push({
        content: openReasoningMatch[1].trim(),
        isComplete: false,
      });
      processedText = processedText.replace(openReasoningMatch[0], '');
    }

    const openPlanningMatch = text.match(/\/\*PLANNING\*\/([\s\S]*?)$/);
    if (openPlanningMatch && !text.includes('/*PLANNING*/', openPlanningMatch.index! + 12)) {
      planningBlocks.push({
        content: openPlanningMatch[1].trim(),
        isComplete: false,
      });
      processedText = processedText.replace(openPlanningMatch[0], '');
    }

    // 检查是否有未闭合的 ACTION 标记（正在生成中），也需要移除
    const openActionMatch = text.match(/\/\*ACTION\*\/([\s\S]*?)$/);
    if (openActionMatch && !text.includes('/*ACTION*/', openActionMatch.index! + 10)) {
      processedText = processedText.replace(openActionMatch[0], '');
    }

    return { processedText: processedText.trim(), reasoningBlocks, planningBlocks, hasFinalAnswer };
  };

  const { processedText, reasoningBlocks, planningBlocks, hasFinalAnswer } = parseContent(content);
  const hasThinkingBlocks = reasoningBlocks.length > 0 || planningBlocks.length > 0 || hasFinalAnswer;

  // 当消息输出完成后自动折叠（但最后一条消息不折叠，包含 FINAL_ANSWER 的消息也不折叠）
  useEffect(() => {
    if (!isLoading && !isGenerating && message?.content && !isCurrentMessage && !hasFinalAnswer) {
      setCollapsed(true);
    }
  }, [isLoading, isGenerating, message?.content, isCurrentMessage, hasFinalAnswer]);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  // 自定义表格渲染器
  const customMarkdownRenderers = {
    ...markdownTagRenderers,
    table: ({ children }: any) => (
      <div className="table-container">
        <table>{children}</table>
      </div>
    ),
    code: (props: any) => <CodeBlock {...props} />,
  };

  // 获取折叠标题和状态
  const getCollapseTitleWithStatus = () => {
    let title = 'AI 回复';
    let isComplete = true;

    // 如果包含 FINAL_ANSWER，优先显示"结果输出"
    if (hasFinalAnswer) {
      title = '结果输出';
      // FINAL_ANSWER 的完成状态取决于消息是否还在生成
      isComplete = !isLoading && !isGenerating;
    } else if (reasoningBlocks.length > 0) {
      title = '推理过程';
      isComplete = reasoningBlocks.every(block => block.isComplete);
      // 如果消息已经完成输出，强制设置为完成状态
      if (!isLoading && !isGenerating) {
        isComplete = true;
      }
    } else if (planningBlocks.length > 0) {
      title = '执行计划';
      isComplete = planningBlocks.every(block => block.isComplete);
      // 如果消息已经完成输出，强制设置为完成状态
      if (!isLoading && !isGenerating) {
        isComplete = true;
      }
    }

    return { title, isComplete };
  };

  const { title: collapseTitle, isComplete: titleIsComplete } = getCollapseTitleWithStatus();

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {content && (
        <div className="copilotKitMessage copilotKitAssistantMessage">
          {/* 带标记的消息：显示折叠/展开功能 */}
          {hasThinkingBlocks ? (
            <>
              {/* 折叠/展开按钮 */}
              <div className={styles.process} onClick={toggleCollapse}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <BuglyCopilotProcessIcon />
                  <span>{collapseTitle}</span>
                </div>
                <div>
                  {!titleIsComplete ? (
                    <span style={{ color: '#1890ff', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                      <LoadingOutlined style={{ fontSize: 14 }} />
                    </span>
                  ) : (
                    <span style={{ color: '#52c41a', fontSize: '14px', display: 'flex', alignItems: 'center' }}><SuccessIcon /></span>
                  )}
                </div>
              </div>

              {/* 消息内容 */}
              {!collapsed && (
                <>
                  {/* 思考过程区域 - 只在非 FINAL_ANSWER 消息中显示 */}
                  {!hasFinalAnswer && (reasoningBlocks.length > 0 || planningBlocks.length > 0) && (
                    <div style={{ marginBottom: '12px' }}>
                      {reasoningBlocks.map((block, idx) => (
                        <div key={`reasoning-${idx}`} style={{
                          padding: '12px',
                          marginTop: '4px',
                          background: '#fafafa',
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: '#666',
                          lineHeight: '1.6',
                        }}>
                          <Markdown content={block.content} components={customMarkdownRenderers} />
                        </div>
                      ))}
                      {planningBlocks.map((block, idx) => (
                        <div key={`planning-${idx}`} style={{
                          padding: '12px',
                          marginTop: '4px',
                          background: '#fafafa',
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: '#666',
                          lineHeight: '1.6',
                        }}>
                          <Markdown content={block.content} components={customMarkdownRenderers} />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 正常内容 */}
                  {processedText && <Markdown content={processedText} components={customMarkdownRenderers} />}
                </>
              )}
            </>
          ) : (
            <>
              {/* 普通消息：直接显示内容 */}
              <Markdown content={processedText} components={customMarkdownRenderers} />
            </>
          )}
        </div>
      )}
      {subComponent && <div style={{ marginBottom: '0.5rem' }}>{subComponent}</div>}
      {isLoading && <LoadingIcon />}
    </>
  );
};
